/**
 * Defera Capital checkout integrity: the server's own reading of what a
 * checkout asks it to collect, checked before a card is charged or a
 * checkout snapshot is written.
 *
 * Both Capital rails (Stripe: defera-capital-create-subscription; NMI:
 * defera-capital-nmi-create-checkout) build the payment plan from money
 * fields the browser sends: the trip total, the deposit, the installments
 * and the fee breakdown. Until this module the servers checked only that the
 * total was positive (NMI) or at least $10 (Stripe), so an edited request
 * could set up a plan, and a booking, for less than the trip costs.
 *
 * Two layers, both read from the request itself:
 *
 *  1. The plan (always enforced). What the rail will charge adds up to the
 *     total: NMI charges the deposit today and one seeded row per schedule
 *     date, so deposit + every row must equal the total, and the dates must
 *     fall between today and five days before departure. The deposit meets
 *     the lead-time tier. The breakdown (pre-hold subtotal + 15% hold + any
 *     Faye premium) adds up to the total. Review.jsx always satisfies these
 *     to the cent, so a failure is an edited or broken request.
 *
 *  2. The price (enforced unless CAPITAL_PRICE_CHECK=alert; ZERO_PRICE_CHECK
 *     for Zero). The pre-hold subtotal is re-derived from the fare, seats and
 *     airline credits the request carries, with Review's own rules
 *     (calculateReviewPagePricing in src/utils/index.ts, then
 *     applyDiscountToSubtotal in src/utils/discountCodes.js): the tiered
 *     service fee, waived for Flight + Hotel (a Duffel-priced stay of the
 *     customer's that no other trip has used) or a legacy promo the server
 *     stamped on this offer's cart, less a discount code the server stamped
 *     there too. The fare itself is checked against the server's own SerpAPI
 *     quote for the same flights, fare brand and travelers
 *     (checkout-fare-quote.js); when that quote can't be had the checkout goes
 *     ahead, flagged for ops. Airline credits can't be checked here at all:
 *     every one is flagged for ops to verify with the airline before
 *     ticketing, and none may exceed the fare. A database read that fails
 *     refuses only the check it was needed for, with a retry message; it
 *     never switches the price layer off.
 *
 * Defera Zero (defera-zero-create-subscription) gets the same two layers,
 * shaped to its plan: Stripe bills ceil(total / N) every cycle until
 * departure − 21 days, all worked out on the server from the total, the
 * departure date and the frequency, so the browser's total and departure
 * date are the whole plan. Its plan layer checks the 15% hold against the
 * total and the departure date against the itinerary; its price layer
 * re-derives the total with Zero's flat 11% service fee (ZERO_PRICE_CHECK
 * switches it, independently of Capital's).
 *
 * Defera Miles ride both products the same way: a checkout that spends
 * Miles carries them on flight_data (miles-redemption.js), the price is
 * re-derived with their value taken off after any discount code, and the
 * claim itself is checked in the plan layer (always enforced): it adds up at
 * the trip rate and leaves the plan at least $25 (a trip Miles cover entirely
 * is booked with Miles alone, server/miles-only-booking.js). Whether the
 * customer actually has the Miles is settled by the debit, before the card is
 * charged.
 *
 * Plain JS: Node (tests, the Express twins), Deno (the checkout functions)
 * and the browser share it. The fee, hold, tier and schedule rules mirror
 * src/utils/index.ts and src/utils/paymentScheduleCalculator.js; the parity
 * tests run both over a grid so the two cannot drift.
 */

import { checkMilesClaim, readCheckoutMiles } from './miles-redemption.js';
import { fareToleranceCents, loadFareQuote } from './checkout-fare-quote.js';
import { checkFareOptionIntegrity, FARE_INTEGRITY_MESSAGE } from './fare-option-integrity.js';

/** Defera Capital's hold fee on the pre-hold subtotal (DEFERA_CAPITAL_HOLD_FEE_RATE). */
export const CAPITAL_HOLD_FEE_RATE = 0.15;
/** The last installment is due on or before departure minus this many days. */
export const CAPITAL_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE = 5;
/** Plans are offered from this many days before departure. */
export const CAPITAL_MIN_LEAD_DAYS = 22;
/** The most installments either frequency can produce (26 weekly, 13 bi-weekly). */
export const CAPITAL_MAX_INSTALLMENTS = { weekly: 26, biweekly: 13 };

/** Defera Zero's flat service fee on (fare after credits + seats): calculateReviewPagePricing's defera_zero branch. */
export const ZERO_SERVICE_FEE_RATE = 0.11;
/** Defera Zero's hold fee on the pre-hold subtotal. */
export const ZERO_HOLD_FEE_RATE = 0.15;

/** Cents of slack for the browser's float math against the server's. */
const CENTS_SLACK = 2;
const DAY_MS = 86_400_000;
/** How far back a customer's stays can explain a Flight + Hotel checkout. */
const STAY_LOOKBACK_DAYS = 14;

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const num = (v) => {
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
};
const isRecord = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

/** Capital's tiered service fee rate on (fare after credits + seats): 30% under $300, else 8%. */
export function capitalServiceFeeRate(amount) {
  return amount < 300 ? 0.3 : 0.08;
}

/** The deposit percentages Capital offers at this lead time (getDeferaCapitalDepositOptionsForLeadDays). */
export function capitalDepositOptionsForLeadDays(leadDays) {
  if (!Number.isFinite(leadDays)) return [5, 10, 20, 30, 40, 50];
  if (leadDays < 22) return [];
  if (leadDays < 30) return [60];
  if (leadDays < 60) return [40, 50];
  if (leadDays < 90) return [20, 30, 40, 50];
  return [5, 10, 20, 30, 40, 50];
}

/** YYYY-MM-DD (or an ISO timestamp) → whole days since the epoch, or null. */
export function dayNumber(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''));
  if (!m) return null;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isFinite(t) ? Math.round(t / DAY_MS) : null;
}

/**
 * The installment dates an NMI checkout will seed, read from the browser's
 * client_payment_schedule the way seedInstallmentRows reads them
 * (resolveScheduleDates in nmi-installment-engine.ts): the chosen frequency's
 * plan, then whichever plan has dates, then the legacy flat list.
 */
export function nmiScheduleDates(clientSchedule, paymentFrequency) {
  const cs = isRecord(clientSchedule) ? clientSchedule : {};
  const dates = (branch) => (isRecord(branch) && Array.isArray(branch.schedule) ? branch.schedule.filter(Boolean) : []);
  const chosen = paymentFrequency === 'biweekly' ? dates(cs.biweeklyPlan)
    : paymentFrequency === 'weekly' ? dates(cs.weeklyPlan) : [];
  if (chosen.length) return chosen;
  const weekly = dates(cs.weeklyPlan);
  if (weekly.length) return weekly;
  const biweekly = dates(cs.biweeklyPlan);
  if (biweekly.length) return biweekly;
  return Array.isArray(cs.schedule) ? cs.schedule.filter(Boolean) : [];
}

/** Seat prices on a checkout's flight_data, in dollars: Review's seatTotalCost. */
export function seatTotalFromFlightData(flightData) {
  const detail = isRecord(flightData) ? flightData.seat_selections_detail : null;
  if (!isRecord(detail)) return 0;
  let total = 0;
  for (const seats of Object.values(detail)) {
    if (!isRecord(seats)) continue;
    for (const seat of Object.values(seats)) {
      const price = isRecord(seat) ? seat.price : null;
      total += isRecord(price) && 'amount' in price ? num(price.amount) : num(price || 0);
    }
  }
  return total;
}

/**
 * Airline credits applied at checkout, in dollars: the sum of the credits
 * listed (Review's creditsAppliedTotal). Never the stated credits_total: that
 * is one more browser number, and a total that disagrees with its own list
 * is refused in the plan layer (checkCheckoutClaims).
 */
export function airlineCreditsFromFlightData(flightData) {
  if (!isRecord(flightData)) return 0;
  const credits = Array.isArray(flightData.credits) ? flightData.credits : [];
  return credits.reduce((sum, c) => sum + Math.max(0, num(isRecord(c) ? c.claimedAmount : 0)), 0);
}

/** A seat's price as the browser sent it: dollars, and whether it is a usable figure. */
function seatPrice(seat) {
  const price = isRecord(seat) ? seat.price : null;
  if (price === null || price === undefined || price === '') return { dollars: 0, valid: true };
  const raw = isRecord(price) && 'amount' in price ? price.amount : price;
  const currency = isRecord(price) && typeof price.currency === 'string' ? price.currency.trim().toUpperCase() : '';
  const n = typeof raw === 'string' ? Number(raw.trim()) : Number(raw);
  if (raw === null || raw === undefined || raw === '') return { dollars: 0, valid: true };
  return { dollars: n, valid: Number.isFinite(n) && n >= 0 && (!currency || currency === 'USD') };
}

/**
 * The plan layer's read of the seats and airline credits the checkout claims.
 * Neither can be priced here (no seat map or airline ledger is reachable from
 * the checkout), so these refuse only what no honest Review request carries:
 * a negative, unreadable or foreign-currency seat price, an airline credit
 * with no reference or a non-positive amount, and a credits_total that
 * disagrees with the credits listed (it used to override them).
 *
 * @returns {IntegrityFinding[]}
 */
export function checkCheckoutClaims(flightData) {
  /** @type {IntegrityFinding[]} */
  const findings = [];
  const add = (code, message, detail) => findings.push({ code, layer: 'plan', message, detail });
  const fd = isRecord(flightData) ? flightData : {};

  const detail = isRecord(fd.seat_selections_detail) ? fd.seat_selections_detail : {};
  const badSeats = [];
  for (const [segment, seats] of Object.entries(detail)) {
    if (!isRecord(seats)) continue;
    for (const [designator, seat] of Object.entries(seats)) {
      if (!seatPrice(seat).valid) badSeats.push({ segment, seat: designator, price: isRecord(seat) ? seat.price ?? null : null });
    }
  }
  if (badSeats.length > 0) {
    add('seat_price_invalid', 'A seat on the checkout has a negative, unreadable or non-USD price.', { seats: badSeats.slice(0, 10) });
  }

  const credits = Array.isArray(fd.credits) ? fd.credits : [];
  const badCredits = credits
    .map((c, i) => ({ c, i }))
    .filter(({ c }) => !isRecord(c) || !(num(c.claimedAmount) > 0) || !Number.isFinite(Number(c.claimedAmount))
      || !String(c.reference ?? '').trim())
    .map(({ c, i }) => ({ index: i, claimed_amount: isRecord(c) ? c.claimedAmount ?? null : null, has_reference: isRecord(c) && !!String(c.reference ?? '').trim() }));
  if (badCredits.length > 0) {
    add('credit_invalid', 'An airline credit on the checkout has no reference or no positive amount.', { credits: badCredits });
  }
  // The fare must be the airline's own, for these flights (founder,
  // 2026-10-28: separate tickets are fine, third-party sellers never are).
  // Refused: any seller that isn't an airline flying the ticket, a fare
  // stamped for other flights, and, on separate tickets, a ticket whose
  // flight numbers aren't the flights shown (DS68IN, MDD3YA, 4FLGZU,
  // AKZPQY). A single-purchase fare whose numbers differ is left to the
  // fare-options page: codeshares (AF 8008 = KQ 756) look like that on a
  // saved fare with no stamp. No fare option at all (uploaded itineraries)
  // is left to the price layer.
  const fareIntegrity = checkFareOptionIntegrity({ bookingOption: fd.bookingOption, slices: fd.slices });
  const fareProblems = fareIntegrity.problems.filter((p) => p.code === 'seller_not_airline'
    || p.code === 'fare_for_other_flights'
    || (fareIntegrity.separateTickets && (p.code === 'flights_mismatch' || p.code === 'flights_unverified')));
  if (fareProblems.length > 0) {
    add('fare_option_integrity', 'The fare is sold by a third party, or was picked for other flights.', {
      separate_tickets: fareIntegrity.separateTickets,
      problems: fareProblems.slice(0, 6),
    });
  }

  const listed = Math.round(airlineCreditsFromFlightData(fd) * 100);
  const stated = fd.credits_total;
  if (stated !== undefined && stated !== null && stated !== '') {
    const statedCents = Math.round(Number(stated) * 100);
    if (!Number.isFinite(statedCents) || Math.abs(statedCents - listed) > CENTS_SLACK) {
      add('credits_total_mismatch', 'The airline credit total does not match the credits listed.', {
        credits_total: stated, listed_credits_cents: listed, credits_listed: credits.length,
      });
    }
  }
  return findings;
}

/** The last four characters of a credit reference, for an alert that must not carry the whole number. */
const maskReference = (ref) => {
  const s = String(ref ?? '').trim();
  return s ? `…${s.slice(-4)}` : null;
};

/**
 * Airline credits come off the fare on the customer's word: nothing at
 * checkout can check one with the airline. So none may exceed the airline
 * fare (it can't pay for a hotel, seats or our fees), and every one is
 * flagged for ops to verify before ticketing.
 */
function checkAirlineCredits(fd, fareCents, add) {
  const credits = Array.isArray(fd.credits) ? fd.credits.filter(isRecord) : [];
  if (credits.length === 0) return;
  const creditCents = Math.round(airlineCreditsFromFlightData(fd) * 100);
  if (creditCents > fareCents + CENTS_SLACK) {
    add('credits_exceed_fare', 'The airline credits are more than the airline fare.', {
      credit_cents: creditCents, fare_cents: fareCents,
    });
  }
  add('airline_credit_unverified', 'Airline credits were taken off the fare. Verify each with the airline before ticketing.', {
    credit_cents: creditCents,
    credits: credits.map((c) => ({
      airline: c.airlineCode ?? c.airline ?? null,
      type: c.creditType ?? null,
      claimed_amount: c.claimedAmount ?? null,
      reference: maskReference(c.reference),
      // The browser's word only: nothing has verified a credit at checkout.
      claimed_status: c.status ?? null,
    })),
  });
}

/** A discount code the checkout carries (readCartDiscount's fields), in cents, else 0. */
export function claimedDiscountCents(flightData) {
  if (!isRecord(flightData)) return 0;
  const code = typeof flightData.discount_code === 'string' ? flightData.discount_code.trim() : '';
  const cents = Math.max(0, Math.round(Number(flightData.discount_cents) || 0));
  return code && cents > 0 ? cents : 0;
}

/** The Miles a checkout spends, in cents off the trip, else 0. An invalid claim counts as none here: the plan layer refuses it. */
export function claimedMilesDiscountCents(flightData) {
  const claim = readCheckoutMiles(flightData);
  return claim && claim.valid ? claim.discountCents : 0;
}

/**
 * Review's travel pricing on Defera Capital, in dollars as the browser
 * computes them: calculateReviewPagePricing's defera_capital branch, then a
 * discount code and then any Miles, each grossed down by the hold rate
 * (applyCheckoutDiscounts). The Faye premium is not part of it; it is added
 * after the hold.
 *
 * @param {{ fare: number, stay?: number, seats?: number, credits?: number,
 *   waiveServiceFee?: boolean, discountCents?: number, milesDiscountCents?: number }} p
 * @returns {{ serviceFee: number, subtotalBeforeHold: number, holdFee: number, grandTotal: number }}
 */
export function capitalTravelPricing({ fare, stay = 0, seats = 0, credits = 0, waiveServiceFee = false, discountCents = 0, milesDiscountCents = 0 }) {
  const baseFareAfterCredits = Math.max(0, fare + stay - credits);
  const feeBase = baseFareAfterCredits + seats;
  const serviceFee = waiveServiceFee ? 0 : feeBase * capitalServiceFeeRate(feeBase);
  const subtotal = baseFareAfterCredits + serviceFee + seats;
  const holdFee = subtotal * CAPITAL_HOLD_FEE_RATE;
  return withDiscounts({ serviceFee, subtotalBeforeHold: subtotal, holdFee, grandTotal: subtotal + holdFee }, [discountCents, milesDiscountCents]);
}

/**
 * Review's travel pricing on Defera Zero, in dollars as the browser computes
 * them: calculateReviewPagePricing's defera_zero branch (a flat 11% service
 * fee, waived for a legacy promo, and a 15% hold), then a discount code and
 * then any Miles, each grossed down by the hold rate. Zero is never
 * Flight + Hotel (resolveCheckoutPaymentMethod), so there is no stay.
 *
 * @param {{ fare: number, seats?: number, credits?: number,
 *   waiveServiceFee?: boolean, discountCents?: number, milesDiscountCents?: number }} p
 * @returns {{ serviceFee: number, subtotalBeforeHold: number, holdFee: number, grandTotal: number }}
 */
export function zeroTravelPricing({ fare, seats = 0, credits = 0, waiveServiceFee = false, discountCents = 0, milesDiscountCents = 0 }) {
  const baseFareAfterCredits = Math.max(0, fare - credits);
  const serviceFee = waiveServiceFee ? 0 : (baseFareAfterCredits + seats) * ZERO_SERVICE_FEE_RATE;
  const subtotal = baseFareAfterCredits + serviceFee + seats;
  const holdFee = subtotal * ZERO_HOLD_FEE_RATE;
  return withDiscounts({ serviceFee, subtotalBeforeHold: subtotal, holdFee, grandTotal: subtotal + holdFee }, [discountCents, milesDiscountCents]);
}

/**
 * Dollar reductions off a financed rail's pricing, in order (a discount code,
 * then Miles), the way Review applies them (applyCheckoutDiscounts in
 * src/utils/discountCodes.js): each taken off the pre-hold subtotal grossed
 * down by the hold rate, so the total falls by its value. The rate is read
 * once, from the undiscounted pricing, for every step. The service fee stays
 * the undiscounted one.
 */
function withDiscounts(pricing, discountsCents) {
  const steps = discountsCents.map((c) => Math.max(0, Math.round(Number(c) || 0))).filter((c) => c > 0);
  if (steps.length === 0) return pricing;
  const rate = pricing.subtotalBeforeHold > 0 ? pricing.holdFee / pricing.subtotalBeforeHold : 0;
  let subtotal = Math.max(0, pricing.subtotalBeforeHold);
  let grandTotal = pricing.grandTotal;
  for (const cents of steps) {
    const applied = Math.min(Math.round(cents / (1 + rate)) / 100, subtotal);
    subtotal = round2(subtotal - applied);
    grandTotal = round2(grandTotal - round2(applied * (1 + rate)));
  }
  return {
    serviceFee: pricing.serviceFee,
    subtotalBeforeHold: subtotal,
    holdFee: round2(subtotal * rate),
    grandTotal,
  };
}

/**
 * The cents the browser sends for that pricing (Review.jsx's checkout props),
 * with any Faye premium added after the hold. Defera Zero sends the same
 * props and never carries a premium.
 *
 * @param {{ serviceFee: number, subtotalBeforeHold: number, holdFee: number, grandTotal: number }} pricing
 * @param {number} [premiumCents]
 */
export function capitalCheckoutCents(pricing, premiumCents = 0) {
  const premium = Math.max(0, Number(premiumCents) || 0) / 100;
  const grand = premium > 0 ? Math.round((pricing.grandTotal + premium) * 100) / 100 : pricing.grandTotal;
  return {
    totalCents: Math.round(grand * 100),
    subtotalBeforeHoldCents: Math.round(pricing.subtotalBeforeHold * 100),
    holdFeeCents: Math.round(pricing.holdFee * 100),
    serviceFeeCents: Math.round(pricing.serviceFee * 100),
  };
}

/**
 * The smallest deposit percentage the request may carry for a departure
 * `leadDays` out on the server's (UTC) calendar, or null when no plan is
 * offered. The browser counts from the customer's own date, which can be a
 * day ahead of UTC, so the tier is read one day further out.
 */
export function minimumCapitalDepositPercent(leadDays) {
  const options = capitalDepositOptionsForLeadDays(leadDays + 1);
  return options.length > 0 ? Math.min(...options) : null;
}

/**
 * @typedef {{ code: string, layer: 'plan' | 'price', message: string,
 *   detail?: Record<string, unknown> }} IntegrityFinding
 */

/**
 * Layer 1: the plan the request describes, checked against itself.
 *
 * @param {{
 *   rail: 'stripe' | 'nmi',
 *   totalCents: number, depositCents: number,
 *   subtotalBeforeHoldCents: number, holdFeeCents: number, premiumCents?: number,
 *   installmentCount: number, installmentAmountCents?: number,
 *   scheduleDates?: string[] | null, paymentFrequency: string,
 *   departureDate: string, today: string,
 *   sliceMode?: boolean,
 * }} p
 * @returns {IntegrityFinding[]}
 */
export function checkCapitalPlan(p) {
  /** @type {IntegrityFinding[]} */
  const findings = [];
  const add = (code, message, detail) => findings.push({ code, layer: 'plan', message, detail });
  const total = Math.round(num(p.totalCents));
  const deposit = Math.round(num(p.depositCents));
  const subtotal = Math.round(num(p.subtotalBeforeHoldCents));
  const hold = Math.round(num(p.holdFeeCents));
  const premium = Math.max(0, Math.round(num(p.premiumCents)));
  const count = Math.floor(num(p.installmentCount));
  const frequency = p.paymentFrequency === 'biweekly' ? 'biweekly' : 'weekly';

  // The breakdown: subtotal + 15% hold (+ premium) is the total.
  const expectedHold = Math.round(subtotal * CAPITAL_HOLD_FEE_RATE);
  if (subtotal <= 0 || Math.abs(hold - expectedHold) > CENTS_SLACK
    || Math.abs(subtotal + hold + premium - total) > CENTS_SLACK) {
    add('breakdown_mismatch', 'The fee breakdown does not add up to the trip total.', {
      subtotal_before_hold_cents: subtotal, hold_fee_cents: hold, expected_hold_fee_cents: expectedHold,
      premium_cents: premium, total_amount_cents: total,
    });
  }

  if (deposit > total) {
    add('deposit_exceeds_total', 'The deposit is larger than the trip total.', { deposit_cents: deposit, total_amount_cents: total });
  }

  // The deposit meets the lead-time tier, on the pre-hold subtotal.
  const lead = (dayNumber(p.departureDate) ?? NaN) - (dayNumber(p.today) ?? NaN);
  const minPct = Number.isFinite(lead) ? minimumCapitalDepositPercent(lead) : null;
  if (Number.isFinite(lead) && minPct === null) {
    add('lead_time_too_short', `Payment plans start ${CAPITAL_MIN_LEAD_DAYS} days before departure.`, { lead_days: lead });
  } else if (minPct !== null && subtotal > 0) {
    const minDeposit = Math.round(subtotal * minPct / 100);
    if (deposit < minDeposit - 1) {
      add('deposit_below_minimum', `The deposit is below the ${minPct}% minimum for this departure.`, {
        deposit_cents: deposit, minimum_deposit_cents: minDeposit, minimum_percent: minPct, lead_days: lead,
      });
    }
  }

  if (count > CAPITAL_MAX_INSTALLMENTS[frequency]) {
    add('too_many_installments', 'The plan has more installments than the schedule allows.', { installment_count: count, frequency });
  }

  // Stripe bills ceil((total − deposit) / N) per cycle in slice mode, which
  // adds up by construction. Its older calendar mode bills the whole total
  // across the cycles on top of the deposit; checkout never asks for it.
  if (p.rail === 'stripe' && p.sliceMode !== true) {
    add('plan_total_mismatch', 'The installments would collect the whole total on top of the deposit.', {
      slice_installments_mode: p.sliceMode ?? null, deposit_cents: deposit, total_amount_cents: total,
    });
  }

  if (p.rail === 'nmi') findings.push(...checkNmiSchedule({ ...p, total, deposit, count }));
  return findings;
}

/**
 * NMI charges the deposit now (the whole total when there is no deposit)
 * and seeds one row per schedule date at the stated amount; nothing else
 * collects the balance, so those must add up to the total.
 */
function checkNmiSchedule({ total, deposit, count, installmentAmountCents, scheduleDates, departureDate, today, paymentFrequency }) {
  /** @type {IntegrityFinding[]} */
  const findings = [];
  const add = (code, message, detail) => findings.push({ code, layer: 'plan', message, detail });
  const amount = Math.round(num(installmentAmountCents));
  const dates = Array.isArray(scheduleDates) ? scheduleDates.filter(Boolean) : [];
  // seedInstallmentRows only seeds when both are positive, one row per date.
  const seeds = count > 0 && amount > 0 ? dates.length : 0;

  if (count > 0 && dates.length !== count) {
    add('schedule_incomplete', 'The payment schedule does not list a date for every installment.', {
      installment_count: count, schedule_dates: dates.length,
    });
  }

  const upfront = deposit > 0 ? deposit : total;
  const collected = upfront + seeds * amount;
  if (Math.abs(collected - total) > Math.max(seeds, 1) + CENTS_SLACK) {
    add('plan_total_mismatch', 'The deposit and installments do not add up to the trip total.', {
      deposit_cents: deposit, installment_count: seeds, installment_amount_cents: amount,
      collected_cents: collected, total_amount_cents: total,
    });
  }

  if (dates.length > 0) {
    const days = dates.map(dayNumber);
    const todayN = dayNumber(today);
    const departN = dayNumber(departureDate);
    const step = paymentFrequency === 'biweekly' ? 14 : 7;
    const bad = days.some((d) => d === null)
      || days.some((d, i) => i > 0 && d - days[i - 1] !== step)
      || (todayN !== null && days[0] < todayN)
      // One day of slack: the browser reads the departure on its own clock.
      || (departN !== null && days[days.length - 1] > departN - CAPITAL_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE + 1);
    if (bad) {
      add('schedule_out_of_window', `Installments must fall between today and ${CAPITAL_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE} days before departure.`, {
        first: dates[0], last: dates[dates.length - 1], departure_date: departureDate, frequency: paymentFrequency,
      });
    }
  }
  return findings;
}

/** Discount findings by the reason the server couldn't honor the code. */
const DISCOUNT_FINDINGS = {
  not_applied: ['discount_unverified', 'The discount code was not applied to this trip by the server.'],
  unknown_code: ['discount_inactive', 'The discount code does not exist.'],
  inactive: ['discount_inactive', 'The discount code is switched off.'],
  not_started: ['discount_inactive', 'The discount code has not started yet.'],
  expired: ['discount_expired', 'The discount code has expired.'],
  limit_reached: ['discount_limit_reached', 'The discount code has reached its redemption limit.'],
  already_used: ['discount_already_used', 'The customer has already used this discount code on another booking.'],
};

/**
 * The price layer's input checks, shared by both products:
 *  - the base fare the request names is the offer's own total;
 *  - the fare is not below the server's own quote for the same flights,
 *    fare brand and travelers (checkout-fare-quote.js), or the quote is
 *    flagged as unavailable;
 *  - a claimed discount code is one the server stamped on this offer's cart,
 *    live, unexpired, under its limits and not spent on another booking;
 *  - airline credits stay within the fare and are flagged for verification.
 */
function checkFareAndDiscount(fd, fareCents, p, add) {
  const offerCents = Math.round(num(fd.total_amount) * 100);
  if (Math.abs(offerCents - fareCents) > 1) {
    add('fare_mismatch', 'The base fare does not match the flight offer.', { base_fare_cents: fareCents, offer_total_cents: offerCents });
  }
  checkQuotedFare(fareCents, p.fareQuote, add);

  const discountCents = claimedDiscountCents(fd);
  if (discountCents > 0) {
    const detail = { discount_code: fd.discount_code, discount_cents: discountCents };
    const d = isRecord(p.discount) ? p.discount : null;
    if (p.discountLookupFailed) {
      add('discount_lookup_failed', 'The discount code could not be checked (database read failed).', detail);
    } else if (!d || d.verified !== true) {
      const [code, message] = DISCOUNT_FINDINGS[d?.reason] || DISCOUNT_FINDINGS.not_applied;
      add(code, message, { ...detail, reason: d?.reason ?? 'not_checked', code_discount_cents: d?.codeCents ?? null });
    } else if (d.recorded === false) {
      // Stamped by the server, but its ledger row is missing: the apply
      // endpoint keeps the discount when only that write fails.
      add('discount_unrecorded', 'The discount code is applied but no redemption is recorded for this cart.', detail);
    }
  }
  checkAirlineCredits(fd, fareCents, add);
}

/**
 * The fare against the server's quote. Below it by more than the tolerance
 * (the larger of $5 and 2%) is refused when the fare check enforces; a quote
 * the server couldn't get is flagged, never refused.
 */
function checkQuotedFare(fareCents, quote, add) {
  if (!isRecord(quote) || quote.status === 'skipped') return;
  const mode = quote.mode === 'alert' ? 'alert' : 'enforce';
  const travelers = isRecord(quote.travelers)
    ? { priced: quote.travelers.priced, adults: quote.travelers.adults, children: quote.travelers.children, infants_in_seat: quote.travelers.infantsInSeat, lap_infants: quote.travelers.lapInfants }
    : null;
  if (quote.status !== 'ok') {
    add('fare_unverified', 'The server could not price these flights. Check the fare with the airline before ticketing.', {
      reason: quote.reason ?? 'unknown', fare_cents: fareCents, travelers, fare_brand: quote.fareBrand ?? null,
      ...(quote.message ? { message: quote.message } : {}),
    });
    return;
  }
  const quotedCents = Math.round(num(quote.fareCents));
  const toleranceCents = fareToleranceCents(quotedCents);
  if (fareCents < quotedCents - toleranceCents) {
    add('fare_below_quote', 'The fare is below what the airline charges today for these flights and travelers.', {
      fare_cents: fareCents, quoted_fare_cents: quotedCents, difference_cents: quotedCents - fareCents,
      tolerance_cents: toleranceCents, fare_brand: quote.fareBrand ?? null, travelers,
      flights: Array.isArray(quote.flights) ? quote.flights : [], mode,
    });
  }
}

/**
 * Layer 2: the pre-hold subtotal against the fare, seats, credits and fee
 * rules. Tries, in order: the fare with the tiered fee; each of the
 * customer's usable stays with the fee waived (Flight + Hotel: priced from
 * Duffel's own rate, and not already the hotel of another trip); a legacy
 * promo with the fee waived, when the server stamped it on this offer's cart.
 * A waiver the server can't confirm (an unverified stay, an unstamped promo)
 * is refused when enforcing, as is any check whose database read failed.
 *
 * @param {{
 *   fareCents: number, flightData: Record<string, unknown>,
 *   subtotalBeforeHoldCents: number,
 *   stays?: Array<{ id: string, totalCents: number }>,
 *   unverifiedStays?: Array<{ id: string, totalCents: number, reason: string }>,
 *   staysLookupFailed?: boolean,
 *   promoVerified?: boolean | null, promoLookupFailed?: boolean,
 *   discount?: { verified: boolean, reason?: string | null, codeCents?: number | null, recorded?: boolean } | null,
 *   discountLookupFailed?: boolean,
 *   fareQuote?: Record<string, unknown> | null,
 * }} p
 * @returns {{ findings: IntegrityFinding[], basis: 'fare' | 'stay' | 'promo' | null,
 *   stayId: string | null, expectedSubtotalCents: number }}
 */
export function checkCapitalPrice(p) {
  /** @type {IntegrityFinding[]} */
  const findings = [];
  const add = (code, message, detail) => findings.push({ code, layer: 'price', message, detail });
  const fd = isRecord(p.flightData) ? p.flightData : {};
  const fareCents = Math.round(num(p.fareCents));
  const claimed = Math.round(num(p.subtotalBeforeHoldCents));

  checkFareAndDiscount(fd, fareCents, p, add);
  const discountCents = claimedDiscountCents(fd);
  const milesDiscountCents = claimedMilesDiscountCents(fd);

  const inputs = {
    fare: fareCents / 100,
    seats: seatTotalFromFlightData(fd),
    credits: airlineCreditsFromFlightData(fd),
    discountCents,
    milesDiscountCents,
  };
  const subtotalFor = (extra) => capitalCheckoutCents(capitalTravelPricing({ ...inputs, ...extra })).subtotalBeforeHoldCents;
  const close = (cents) => Math.abs(cents - claimed) <= CENTS_SLACK;

  const expected = subtotalFor({});
  if (close(expected)) return { findings, basis: 'fare', stayId: null, expectedSubtotalCents: expected };
  const matchesStay = (stay) => close(subtotalFor({ stay: Math.round(num(stay.totalCents)) / 100, waiveServiceFee: true }));
  for (const stay of Array.isArray(p.stays) ? p.stays : []) {
    const stayCents = Math.round(num(stay.totalCents));
    if (matchesStay(stay)) {
      // The waived fee is the package's discount. A hotel cheaper than that
      // fee makes the package cost less than the flight alone: legitimate for
      // a cheap room (its price is Duffel's own), so it is only flagged.
      const waivedFeeCents = Math.round(capitalTravelPricing(inputs).serviceFee * 100);
      if (stayCents < waivedFeeCents) {
        add('stay_below_fee', 'The hotel costs less than the service fee it waives.', {
          stay_id: stay.id, stay_cents: stayCents, waived_fee_cents: waivedFeeCents,
        });
      }
      return { findings, basis: 'stay', stayId: stay.id, expectedSubtotalCents: expected };
    }
  }
  const unverifiedStay = (Array.isArray(p.unverifiedStays) ? p.unverifiedStays : []).find(matchesStay);
  if (unverifiedStay) {
    add('fee_waiver_unverified', unverifiedStay.reason === 'used_by_another_trip'
      ? 'The service fee was waived for a hotel that is already part of another trip.'
      : 'The service fee was waived for a hotel whose price the server did not get from Duffel.', {
      waiver: 'stay', reason: unverifiedStay.reason, stay_id: unverifiedStay.id, stay_cents: Math.round(num(unverifiedStay.totalCents)),
      subtotal_before_hold_cents: claimed, subtotal_with_fee_cents: expected,
    });
    return { findings, basis: null, stayId: null, expectedSubtotalCents: expected };
  }
  if (fd.promo_applied === true && close(subtotalFor({ waiveServiceFee: true }))) {
    if (p.promoVerified === true) return { findings, basis: 'promo', stayId: null, expectedSubtotalCents: expected };
    if (p.promoLookupFailed) {
      add('promo_lookup_failed', 'The promo could not be checked (database read failed).', { subtotal_before_hold_cents: claimed });
    } else {
      add('fee_waiver_unverified', 'The service fee was waived by a promo the server did not apply to this trip.', {
        waiver: 'promo', subtotal_before_hold_cents: claimed, subtotal_with_fee_cents: expected,
      });
    }
    return { findings, basis: null, stayId: null, expectedSubtotalCents: expected };
  }
  if (p.staysLookupFailed) {
    // The price may be a Flight + Hotel package whose stay couldn't be read.
    add('stay_lookup_failed', 'The hotel could not be checked (database read failed).', {
      subtotal_before_hold_cents: claimed, expected_subtotal_cents: expected,
    });
    return { findings, basis: null, stayId: null, expectedSubtotalCents: expected };
  }
  add('price_mismatch', claimed < expected
    ? 'The trip is priced below its fare, seats and fees.'
    : 'The trip is priced above its fare, seats and fees.', {
    subtotal_before_hold_cents: claimed, expected_subtotal_cents: expected,
    difference_cents: claimed - expected, direction: claimed < expected ? 'under' : 'over',
    fare_cents: fareCents, seat_cents: Math.round(inputs.seats * 100), credit_cents: Math.round(inputs.credits * 100),
    discount_cents: discountCents, miles_discount_cents: milesDiscountCents,
    stays_considered: Array.isArray(p.stays) ? p.stays.length : 0,
  });
  return { findings, basis: null, stayId: null, expectedSubtotalCents: expected };
}

/**
 * Price findings that refuse the checkout while the price layer enforces.
 * Everything else here is flagged for ops: a quote the server couldn't get,
 * airline credits to verify, a cheap hotel, a price above its parts.
 */
const ENFORCEABLE_PRICE_CODES = new Set([
  'fare_mismatch',
  'discount_unverified', 'discount_inactive', 'discount_expired', 'discount_limit_reached', 'discount_already_used',
  'fee_waiver_unverified', 'credits_exceed_fare',
  'discount_lookup_failed', 'promo_lookup_failed', 'stay_lookup_failed',
]);

/** Findings that mean a database read failed: the customer is asked to try again. */
export const LOOKUP_FAILED_CODES = new Set(['discount_lookup_failed', 'promo_lookup_failed', 'stay_lookup_failed']);

/** Price findings that reject the checkout when CAPITAL_PRICE_CHECK (Zero: ZERO_PRICE_CHECK) enforces. */
export function isEnforceablePriceFinding(finding) {
  if (ENFORCEABLE_PRICE_CODES.has(finding.code)) return true;
  // CHECKOUT_FARE_CHECK=alert keeps the quote's verdict to an alert.
  if (finding.code === 'fare_below_quote') return finding.detail?.mode !== 'alert';
  return finding.code === 'price_mismatch' && finding.detail?.direction === 'under';
}

/**
 * How the price layer runs, from CAPITAL_PRICE_CHECK or ZERO_PRICE_CHECK.
 * Enforces unless told 'alert' (the rollback): unset, empty or a typo all
 * enforce, so a missing variable can't quietly switch the check off.
 */
export function priceCheckMode(value) {
  return String(value ?? '').trim().toLowerCase() === 'alert' ? 'alert' : 'enforce';
}

/**
 * Both layers. `reject` lists the findings that stop the checkout before
 * anything is written or charged; `alert` lists findings for ops review
 * that let it continue. A database read that failed refuses only the check
 * that needed it (a `*_lookup_failed` finding, answered with a retry), never
 * the whole price layer.
 *
 * @param {Parameters<typeof checkCapitalPlan>[0] & Parameters<typeof checkCapitalPrice>[0]
 *   & { priceMode?: 'alert' | 'enforce' }} p
 * @returns {{ ok: boolean, reject: IntegrityFinding[], alert: IntegrityFinding[],
 *   basis: 'fare' | 'stay' | 'promo' | null, stayId: string | null, expectedSubtotalCents: number }}
 */
export function evaluateCapitalCheckout(p) {
  const plan = [
    ...checkCapitalPlan(p),
    ...checkCheckoutClaims(p.flightData),
    ...checkMilesClaim({
      flightData: p.flightData,
      travelTotalCents: Math.round(num(p.totalCents)) - Math.max(0, Math.round(num(p.premiumCents))),
    }),
  ];
  const price = checkCapitalPrice(p);
  const enforce = priceCheckMode(p.priceMode) === 'enforce';
  const reject = [...plan, ...price.findings.filter((f) => enforce && isEnforceablePriceFinding(f))];
  const alert = price.findings.filter((f) => !reject.includes(f));
  return { ok: reject.length === 0, reject, alert, basis: price.basis, stayId: price.stayId, expectedSubtotalCents: price.expectedSubtotalCents };
}

/** The itinerary's first departure date (YYYY-MM-DD, airline-local), or null. */
export function flightDepartureDate(flightData) {
  const slices = isRecord(flightData) && Array.isArray(flightData.slices) ? flightData.slices : [];
  const segments = isRecord(slices[0]) && Array.isArray(slices[0].segments) ? slices[0].segments : [];
  const departingAt = isRecord(segments[0]) ? segments[0].departing_at : null;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(departingAt ?? ''));
  return m ? m[1] : null;
}

/**
 * Defera Zero layer 1: the plan. Stripe bills the total in equal cycles the
 * server works out from the departure date, so the checks are on what the
 * request says the trip is: the hold is 15% of what precedes it, and the
 * departure date is the itinerary's own (a later date would move the last
 * payment past departure − 21 days, and the subscription's cancel_at with
 * it). Review always satisfies these. The service fee bills nothing on its
 * own (it is inside the total), so the price layer checks it, alerting only.
 *
 * Zero also carries only fully refundable fares (Defera recovers the ticket
 * from the airline if the customer defaults). The caller decides that with
 * the shared refundability rule (_shared/fare-brand-refundability.ts, or its
 * Node copy), on the server's own quoted fare brand when it has one, and
 * passes `fareRefundable`; false is refused like any plan finding.
 *
 * @param {{ totalCents: number, holdFeeCents: number, serviceFeeCents?: number,
 *   departureDate: string, flightData: Record<string, unknown>, fareRefundable?: boolean }} p
 * @returns {IntegrityFinding[]}
 */
export function checkZeroPlan(p) {
  /** @type {IntegrityFinding[]} */
  const findings = [];
  const add = (code, message, detail) => findings.push({ code, layer: 'plan', message, detail });
  const total = Math.round(num(p.totalCents));
  const hold = Math.round(num(p.holdFeeCents));
  const subtotal = total - hold;

  const expectedHold = Math.round(subtotal * ZERO_HOLD_FEE_RATE);
  if (subtotal <= 0 || Math.abs(hold - expectedHold) > CENTS_SLACK) {
    add('breakdown_mismatch', 'The fee breakdown does not add up to the trip total.', {
      total_amount_cents: total, hold_fee_cents: hold, expected_hold_fee_cents: expectedHold,
      subtotal_before_hold_cents: subtotal,
    });
  }

  // Review reads departure_date off this same segment, so an itinerary with
  // no departure can't be its request either.
  const departs = flightDepartureDate(p.flightData);
  const claimedDeparture = /^(\d{4}-\d{2}-\d{2})/.exec(String(p.departureDate ?? ''))?.[1] ?? null;
  if (departs === null || claimedDeparture !== departs) {
    add('departure_mismatch', 'The departure date does not match the itinerary.', {
      departure_date: p.departureDate ?? null, itinerary_departure_date: departs,
    });
  }

  if (p.fareRefundable === false) {
    const fd = isRecord(p.flightData) ? p.flightData : {};
    const bo = isRecord(fd.bookingOption) ? fd.bookingOption : {};
    const leg = [bo.together, bo.departing, bo.returning].find(isRecord);
    add('fare_not_refundable', 'Defera Zero needs a fully refundable fare, and this fare brand is not one.', {
      fare_brand: leg?.option_title ?? null,
    });
  }
  return findings;
}

/**
 * Defera Zero layer 2: the total against the fare, seats, credits and Zero's
 * fee rules. Tries the fare with the 11% fee, then a legacy promo with the
 * fee waived (accepted only when the server stamped it on this offer's
 * cart). Zero is never Flight + Hotel, so a hotel in the price reads as priced
 * above its parts: flagged, never refused. A service fee that disagrees with
 * the matched price is flagged too: it changes the booking's reported fees,
 * not what Stripe bills.
 *
 * @param {{
 *   fareCents: number, flightData: Record<string, unknown>,
 *   totalCents: number, serviceFeeCents: number,
 *   promoVerified?: boolean | null, promoLookupFailed?: boolean,
 *   discount?: { verified: boolean, reason?: string | null, codeCents?: number | null, recorded?: boolean } | null,
 *   discountLookupFailed?: boolean,
 *   fareQuote?: Record<string, unknown> | null,
 * }} p
 * @returns {{ findings: IntegrityFinding[], basis: 'fare' | 'promo' | null, expectedTotalCents: number }}
 */
export function checkZeroPrice(p) {
  /** @type {IntegrityFinding[]} */
  const findings = [];
  const add = (code, message, detail) => findings.push({ code, layer: 'price', message, detail });
  const fd = isRecord(p.flightData) ? p.flightData : {};
  const fareCents = Math.round(num(p.fareCents));
  const claimed = Math.round(num(p.totalCents));
  const claimedService = Math.round(num(p.serviceFeeCents));

  checkFareAndDiscount(fd, fareCents, p, add);
  const discountCents = claimedDiscountCents(fd);
  const milesDiscountCents = claimedMilesDiscountCents(fd);

  const inputs = {
    fare: fareCents / 100,
    seats: seatTotalFromFlightData(fd),
    credits: airlineCreditsFromFlightData(fd),
    discountCents,
    milesDiscountCents,
  };
  const centsFor = (extra) => capitalCheckoutCents(zeroTravelPricing({ ...inputs, ...extra }));
  const close = (cents) => Math.abs(cents - claimed) <= CENTS_SLACK;
  const checkServiceFee = (expectedServiceCents) => {
    if (Math.abs(claimedService - expectedServiceCents) > CENTS_SLACK) {
      add('service_fee_mismatch', 'The service fee does not match the trip’s price.', {
        service_fee_cents: claimedService, expected_service_fee_cents: expectedServiceCents,
      });
    }
  };

  const expected = centsFor({});
  if (close(expected.totalCents)) {
    checkServiceFee(expected.serviceFeeCents);
    return { findings, basis: 'fare', expectedTotalCents: expected.totalCents };
  }
  if (fd.promo_applied === true && close(centsFor({ waiveServiceFee: true }).totalCents)) {
    if (p.promoVerified === true) {
      checkServiceFee(0);
      return { findings, basis: 'promo', expectedTotalCents: expected.totalCents };
    }
    if (p.promoLookupFailed) {
      add('promo_lookup_failed', 'The promo could not be checked (database read failed).', { total_amount_cents: claimed });
    } else {
      add('fee_waiver_unverified', 'The service fee was waived by a promo the server did not apply to this trip.', {
        waiver: 'promo', total_amount_cents: claimed, total_with_fee_cents: expected.totalCents,
      });
    }
    return { findings, basis: null, expectedTotalCents: expected.totalCents };
  }
  add('price_mismatch', claimed < expected.totalCents
    ? 'The trip is priced below its fare, seats and fees.'
    : 'The trip is priced above its fare, seats and fees.', {
    total_amount_cents: claimed, expected_total_cents: expected.totalCents,
    difference_cents: claimed - expected.totalCents, direction: claimed < expected.totalCents ? 'under' : 'over',
    fare_cents: fareCents, seat_cents: Math.round(inputs.seats * 100), credit_cents: Math.round(inputs.credits * 100),
    discount_cents: discountCents, miles_discount_cents: milesDiscountCents,
  });
  return { findings, basis: null, expectedTotalCents: expected.totalCents };
}

/**
 * Both Defera Zero layers, on the same terms as evaluateCapitalCheckout: the
 * plan always refuses; the price refuses while enforcing (ZERO_PRICE_CHECK
 * unset or anything but 'alert').
 *
 * @param {Parameters<typeof checkZeroPlan>[0] & Parameters<typeof checkZeroPrice>[0]
 *   & { priceMode?: 'alert' | 'enforce' }} p
 * @returns {{ ok: boolean, reject: IntegrityFinding[], alert: IntegrityFinding[],
 *   basis: 'fare' | 'promo' | null, expectedTotalCents: number }}
 */
export function evaluateZeroCheckout(p) {
  const plan = [
    ...checkZeroPlan(p),
    ...checkCheckoutClaims(p.flightData),
    ...checkMilesClaim({ flightData: p.flightData, travelTotalCents: p.totalCents }),
  ];
  const price = checkZeroPrice(p);
  const enforce = priceCheckMode(p.priceMode) === 'enforce';
  const reject = [...plan, ...price.findings.filter((f) => enforce && isEnforceablePriceFinding(f))];
  const alert = price.findings.filter((f) => !reject.includes(f));
  return { ok: reject.length === 0, reject, alert, basis: price.basis, expectedTotalCents: price.expectedTotalCents };
}

/** What the customer sees when a checkout is refused for a reason not named below. */
export const INTEGRITY_REJECTION_MESSAGE = 'We couldn’t confirm the price for this trip. Please refresh the page and try again. If it keeps happening, contact us at defera.app/contact.';

const NOT_CHARGED = 'Your card was not charged.';
const usd = (cents) => `$${(Math.round(num(cents)) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const travelersLabel = (n) => `${n} traveler${n === 1 ? '' : 's'}`;

/**
 * The response a refused checkout gets: the most useful reason first, with
 * error_type 'checkout_integrity' (both checkout forms show `error` and skip
 * the payment poll for it). A failed database read alone answers 503 so the
 * customer simply tries again.
 *
 * @param {IntegrityFinding[]} reject
 * @returns {{ status: number, body: Record<string, unknown> }}
 */
export function integrityRefusal(reject) {
  const findings = Array.isArray(reject) ? reject : [];
  const codes = findings.map((f) => f.code);
  const find = (...want) => findings.find((f) => want.includes(f.code));
  const body = (status, error, extra = {}) => ({
    status,
    body: { error, error_type: 'checkout_integrity', integrity_codes: codes, ...extra },
  });

  const fareOption = find('fare_option_integrity');
  if (fareOption) {
    return body(409, `${FARE_INTEGRITY_MESSAGE} ${NOT_CHARGED}`, { code: 'FARE_NOT_FOR_THESE_FLIGHTS' });
  }
  const refundable = find('fare_not_refundable');
  if (refundable) {
    return body(400, `Defera Zero needs a fully refundable fare, and this fare isn’t one. Please choose a refundable fare, or pay with a deposit plan instead. ${NOT_CHARGED}`, { code: 'FARE_NOT_REFUNDABLE' });
  }
  const fare = find('fare_below_quote');
  if (fare) {
    const d = fare.detail || {};
    const n = Number(d.travelers?.priced) || 0;
    const forWhom = n > 0 ? ` for ${travelersLabel(n)}` : '';
    return body(409, `The fare for these flights is now ${usd(d.quoted_fare_cents)}${forWhom}, more than the ${usd(d.fare_cents)} shown here. Please search again to see today’s price. ${NOT_CHARGED}`, {
      code: 'FARE_PRICE_CHANGED', quoted_fare_cents: d.quoted_fare_cents ?? null, fare_cents: d.fare_cents ?? null,
    });
  }
  const discount = find('discount_expired', 'discount_limit_reached', 'discount_already_used', 'discount_inactive', 'discount_unverified');
  if (discount) {
    const why = discount.code === 'discount_expired' ? 'This discount code has expired.'
      : discount.code === 'discount_limit_reached' || discount.code === 'discount_already_used' ? 'This discount code has already been used.'
        : 'We couldn’t confirm the discount code on this trip.';
    return body(400, `${why} Please remove it and try again. ${NOT_CHARGED}`, { code: 'DISCOUNT_UNAVAILABLE' });
  }
  const waiver = find('fee_waiver_unverified');
  if (waiver) {
    const stay = waiver.detail?.waiver === 'stay';
    return body(400, stay
      ? `We couldn’t confirm the hotel price on this trip. Please choose your room again, then try again. ${NOT_CHARGED}`
      : `We couldn’t confirm the promo code on this trip. Please remove it, add it again, then try again. ${NOT_CHARGED}`, { code: 'FEE_WAIVER_UNVERIFIED' });
  }
  const credits = find('credits_exceed_fare');
  if (credits) {
    return body(400, `Airline credits can only cover the airline fare (${usd(credits.detail?.fare_cents)}). Please lower the credit amount and try again. ${NOT_CHARGED}`, { code: 'CREDITS_EXCEED_FARE' });
  }
  if (findings.length > 0 && findings.every((f) => LOOKUP_FAILED_CODES.has(f.code))) {
    return body(503, `We couldn’t check this trip’s price just now. Please try again in a moment. ${NOT_CHARGED}`, { code: 'PRICE_CHECK_UNAVAILABLE' });
  }
  return body(400, INTEGRITY_REJECTION_MESSAGE);
}

/** A stay's price came from Duffel's own rate, attested by the server when it was saved (server/stay-rate-attestation.js). */
export function stayPriceVerified(stay) {
  const v = isRecord(stay?.metadata) ? stay.metadata.price_verification : null;
  if (!isRecord(v) || v.source !== 'duffel' || !v.rate_id) return false;
  const cents = Math.round(num(stay.total_amount) * 100);
  return Number.isInteger(v.total_cents) && v.total_cents > 0 && v.total_cents === cents;
}

const sameReference = (a, b) => !!a && !!b && String(a).trim().toUpperCase() === String(b).trim().toUpperCase();

/**
 * The customer's recent stays, split into those that can waive this trip's
 * fee and those that can't. Usable: Duffel-priced (stayPriceVerified) and not
 * the hotel of another trip, i.e. unlinked, or linked to this trip already (a
 * retry). One stay, one trip.
 */
async function loadStays(supabase, { userId, tripReference, now }) {
  const since = new Date(now.getTime() - STAY_LOOKBACK_DAYS * DAY_MS).toISOString();
  const { data, error } = await supabase
    .from('stays')
    .select('id, total_amount, booking_id, metadata')
    .eq('user_id', userId)
    .gte('created_at', since)
    .order('created_at', { ascending: false })
    .limit(20);
  if (error) throw error;
  const rows = (data || []).filter((s) => Math.round(num(s.total_amount) * 100) > 0);

  const linkedIds = [...new Set(rows.map((s) => s.booking_id).filter(Boolean).map(String))];
  const ownBookings = new Set();
  if (linkedIds.length > 0) {
    const linked = await supabase.from('bookings').select('id, trip_reference, booking_reference').in('id', linkedIds);
    if (linked.error) throw linked.error;
    for (const b of linked.data || []) {
      if (sameReference(b.trip_reference, tripReference) || sameReference(b.booking_reference, tripReference)) ownBookings.add(String(b.id));
    }
  }

  const stays = [];
  const unverifiedStays = [];
  for (const s of rows) {
    const stay = { id: String(s.id), totalCents: Math.round(num(s.total_amount) * 100) };
    if (s.booking_id && !ownBookings.has(String(s.booking_id))) unverifiedStays.push({ ...stay, reason: 'used_by_another_trip' });
    else if (!stayPriceVerified(s)) unverifiedStays.push({ ...stay, reason: 'price_unverified' });
    else stays.push(stay);
  }
  return { stays, unverifiedStays };
}

/**
 * This offer's carts: the customer's own, or a guest cart they signed in on
 * (the promo and discount endpoints accept both). The promo waiver and the
 * discount stamp inside flight_data are written only by the server
 * (migration 160's trigger), so they are the server's own record that it
 * applied them to this trip.
 */
async function loadOfferCarts(supabase, { userId, offerId }) {
  if (!offerId || !userId) return [];
  const { data, error } = await supabase
    .from('abandoned_trips')
    .select('id, user_id, flight_data')
    .eq('flight_data->>id', String(offerId))
    .or(`user_id.eq.${userId},user_id.is.null`)
    .order('updated_at', { ascending: false })
    .limit(5);
  if (error) throw error;
  return (data || []).filter((c) => c && isRecord(c.flight_data));
}

const countOf = async (query) => {
  const { count, error } = await query;
  if (error) throw error;
  return Number(count) || 0;
};

/**
 * Whether the server stands behind the checkout's discount code: stamped on
 * this offer's cart at this amount by the apply endpoint; the code live,
 * started and unexpired; under its overall and per-customer limits; and the
 * cart's own redemption not already spent on a different booking. Throws on a
 * failed read (the caller fails that check closed).
 *
 * @returns {Promise<{ verified: boolean, reason: string | null, codeCents: number | null, recorded: boolean }>}
 */
async function verifyDiscount(supabase, { userId, flightData, carts, tripReference, now }) {
  const fd = isRecord(flightData) ? flightData : {};
  const claimed = claimedDiscountCents(fd);
  const code = String(fd.discount_code).trim().toUpperCase();
  const result = (verified, reason, extra = {}) => ({ verified, reason, codeCents: null, recorded: true, ...extra });

  const cart = carts.find((c) => String(c.flight_data.discount_code ?? '').trim().toUpperCase() === code
    && Math.round(Number(c.flight_data.discount_cents) || 0) === claimed);
  if (!cart) return result(false, 'not_applied');

  const codeRow = await supabase
    .from('discount_codes')
    .select('id, active, starts_at, expires_at, discount_type, discount_cents, max_redemptions, per_customer_limit')
    .eq('code', code)
    .maybeSingle();
  if (codeRow.error) throw codeRow.error;
  const row = codeRow.data;
  if (!row) return result(false, 'unknown_code');
  const codeCents = row.discount_cents ?? null;
  const nowMs = now.getTime();
  if (!row.active || (row.discount_type && row.discount_type !== 'fixed_amount')) return result(false, 'inactive', { codeCents });
  if (row.starts_at && Date.parse(row.starts_at) > nowMs) return result(false, 'not_started', { codeCents });
  if (row.expires_at && Date.parse(row.expires_at) <= nowMs) return result(false, 'expired', { codeCents });

  const live = ['applied', 'converted'];
  const [mine, total, all] = await Promise.all([
    supabase.from('discount_code_redemptions').select('status, booking_reference').eq('code_id', row.id).eq('cart_id', cart.id).in('status', live),
    row.max_redemptions != null
      ? countOf(supabase.from('discount_code_redemptions').select('id', { count: 'exact', head: true }).eq('code_id', row.id).in('status', live))
      : Promise.resolve(0),
    row.per_customer_limit != null
      ? countOf(supabase.from('discount_code_redemptions').select('id', { count: 'exact', head: true }).eq('code_id', row.id).eq('user_id', userId).in('status', live))
      : Promise.resolve(0),
  ]);
  if (mine.error) throw mine.error;
  const cartRedemptions = mine.data || [];
  const spentElsewhere = cartRedemptions.length > 0 && !cartRedemptions.some((r) => r.status === 'applied'
    || sameReference(r.booking_reference, tripReference));
  if (spentElsewhere) return result(false, 'already_used', { codeCents });
  // Each count includes this cart's own redemption.
  if (row.max_redemptions != null && total > row.max_redemptions) return result(false, 'limit_reached', { codeCents });
  if (row.per_customer_limit != null && all > row.per_customer_limit) return result(false, 'already_used', { codeCents });
  return result(true, null, { codeCents, recorded: cartRedemptions.length > 0 });
}

/**
 * The cart reads behind a promo waiver and a discount code, when the
 * checkout claims either. Never throws: a failed read sets the matching
 * *LookupFailed flag, and the price layer refuses just that claim with a
 * retry message.
 */
async function loadCartClaims(supabase, { userId, flightData, tripReference, now }) {
  const fd = isRecord(flightData) ? flightData : {};
  const out = { promoVerified: null, promoLookupFailed: false, discount: null, discountLookupFailed: false };
  const wantsPromo = fd.promo_applied === true;
  const wantsDiscount = claimedDiscountCents(fd) > 0;
  if (!wantsPromo && !wantsDiscount) return out;

  let carts = [];
  try {
    carts = await loadOfferCarts(supabase, { userId, offerId: fd.id });
  } catch (e) {
    console.warn('capital-checkout-integrity: cart lookup failed', e instanceof Error ? e.message : e);
    return { ...out, promoLookupFailed: wantsPromo, discountLookupFailed: wantsDiscount };
  }
  if (wantsPromo) out.promoVerified = carts.some((c) => c.flight_data.promo_applied === true);
  if (wantsDiscount) {
    try {
      out.discount = await verifyDiscount(supabase, { userId, flightData: fd, carts, tripReference, now });
    } catch (e) {
      out.discountLookupFailed = true;
      console.warn('capital-checkout-integrity: discount lookup failed', e instanceof Error ? e.message : e);
    }
  }
  return out;
}

/** The legacy fields some callers read (server/miles-only-booking.js): the claimed cents only when the server stands behind them. */
function discountCompat(fd, claims) {
  const claimed = claimedDiscountCents(isRecord(fd) ? fd : {});
  return {
    redeemedDiscountCents: claimed > 0 && claims.discount?.verified === true ? claimed : null,
    codeDiscountCents: claims.discount?.codeCents ?? null,
  };
}

/**
 * What Capital's price layer reads: the customer's usable stays (Flight +
 * Hotel waives the fee and adds the hotel), the cart stamps behind a promo or
 * a discount code, and the server's own quote for the fare
 * (checkout-fare-quote.js), all at once. Never throws; never takes longer than
 * the quote's budget.
 *
 * `fareCheck` is the quote's configuration: { mode (CHECKOUT_FARE_CHECK),
 * serpApiKey, fetchImpl?, timeoutMs?, hedgeMs? }. Left out, no quote runs
 * (callers that only need the database reads).
 *
 * @param {any} supabase  a supabase-js client with the service role
 * @param {{ userId: string, flightData: unknown, now?: Date, tripReference?: string | null,
 *   passengers?: unknown[], fareCheck?: Record<string, unknown> | null }} p
 */
export async function loadCapitalPriceContext(supabase, { userId, flightData, now = new Date(), tripReference = null, passengers = [], fareCheck = null }) {
  const staysRead = loadStays(supabase, { userId, tripReference, now }).then(
    (r) => ({ ...r, staysLookupFailed: false }),
    (e) => {
      console.warn('capital-checkout-integrity: stays lookup failed', e instanceof Error ? e.message : e);
      return { stays: [], unverifiedStays: [], staysLookupFailed: true };
    },
  );
  const [stays, claims, fareQuote] = await Promise.all([
    staysRead,
    loadCartClaims(supabase, { userId, flightData, tripReference, now }),
    fareCheck ? loadFareQuote({ ...fareCheck, flightData, passengers }) : Promise.resolve(null),
  ]);
  return {
    ...stays,
    ...claims,
    ...discountCompat(flightData, claims),
    lookupFailed: stays.staysLookupFailed || claims.promoLookupFailed || claims.discountLookupFailed,
    fareQuote,
  };
}

/**
 * What Defera Zero's price layer reads: the cart stamps behind a promo or a
 * discount code and the server's fare quote (Zero has no Flight + Hotel, so
 * no stays). Never throws.
 *
 * @param {any} supabase  a supabase-js client with the service role
 * @param {{ userId: string, flightData: unknown, now?: Date, tripReference?: string | null,
 *   passengers?: unknown[], fareCheck?: Record<string, unknown> | null }} p
 */
export async function loadZeroPriceContext(supabase, { userId, flightData, now = new Date(), tripReference = null, passengers = [], fareCheck = null }) {
  const [claims, fareQuote] = await Promise.all([
    loadCartClaims(supabase, { userId, flightData, tripReference, now }),
    fareCheck ? loadFareQuote({ ...fareCheck, flightData, passengers }) : Promise.resolve(null),
  ]);
  return {
    ...claims,
    ...discountCompat(flightData, claims),
    lookupFailed: claims.promoLookupFailed || claims.discountLookupFailed,
    fareQuote,
  };
}

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const money = (cents) => {
  const n = Number(cents);
  return Number.isFinite(n) ? `$${(n / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
};

/**
 * The support@ email for a refused or flagged checkout: what was asked for,
 * what the server expected, and every finding with its numbers. Lines with
 * no value are left out. `intro` replaces the opening line when the event
 * isn't a checkout (the booking relay).
 *
 * @param {{ rail: 'stripe' | 'nmi' | 'zero', outcome: 'refused' | 'flagged', tripReference: string,
 *   userId: string, userEmail?: string | null, findings: IntegrityFinding[],
 *   request: { totalCents: number, depositCents?: number, subtotalBeforeHoldCents?: number,
 *     holdFeeCents?: number, serviceFeeCents?: number,
 *     baseFareCents?: number | null, installmentCount?: number | null, installmentAmountCents?: number,
 *     departureDate?: string | null, priceMode?: string },
 *   expectedSubtotalCents?: number | null, expectedTotalCents?: number | null, intro?: string }} p
 */
export function renderIntegrityAlert(p) {
  const zero = p.rail === 'zero';
  const railLabel = zero ? 'Defera Zero (Stripe)' : p.rail === 'nmi' ? 'NMI' : 'Stripe';
  const verb = p.outcome === 'refused' ? 'refused' : 'flagged';
  const subject = zero
    ? `Defera Zero checkout ${verb}: ${p.tripReference}`
    : `Defera Capital checkout ${verb}: ${p.tripReference} (${railLabel})`;
  const r = p.request;
  const moneyOrNull = (cents) => (cents != null ? money(cents) : null);
  const lines = /** @type {Array<[string, string | null]>} */ ([
    ['Trip reference', p.tripReference],
    ['Customer', `${p.userEmail || 'unknown email'} (${p.userId})`],
    ['Rail', railLabel],
    ['Departure', r.departureDate || null],
    ['Trip total asked', money(r.totalCents)],
    ['Trip total expected', moneyOrNull(p.expectedTotalCents)],
    ['Pre-hold subtotal asked', moneyOrNull(r.subtotalBeforeHoldCents)],
    ['Pre-hold subtotal expected', moneyOrNull(p.expectedSubtotalCents)],
    ['Hold fee asked', moneyOrNull(r.holdFeeCents)],
    ['Service fee asked', moneyOrNull(r.serviceFeeCents)],
    ['Base fare', moneyOrNull(r.baseFareCents)],
    ['Deposit', moneyOrNull(r.depositCents)],
    ['Installments', r.installmentCount != null
      ? `${r.installmentCount}${r.installmentAmountCents ? ` × ${money(r.installmentAmountCents)}` : ''}` : null],
    ['Price check', r.priceMode ? (r.priceMode === 'enforce' ? 'enforcing' : 'alerting only') : null],
  ]).filter(([, v]) => v != null);
  const intro = p.intro || (p.outcome === 'refused'
    ? 'The checkout was refused before anything was charged or saved. The customer was asked to refresh and try again.'
    : 'The checkout went ahead. Review the price below before ticketing.');
  const findingsHtml = p.findings.map((f) => `<li><strong>${escapeHtml(f.code)}</strong>: ${escapeHtml(f.message)}`
    + (f.detail ? `<br><code style="font-size:12px">${escapeHtml(JSON.stringify(f.detail))}</code>` : '') + '</li>').join('');
  const html = `<div style="font-family:-apple-system,Segoe UI,sans-serif;font-size:14px;color:#111">
<p>${escapeHtml(intro)}</p>
<table style="border-collapse:collapse">${lines.map(([k, v]) => `<tr><td style="padding:2px 12px 2px 0;color:#555">${escapeHtml(k)}</td><td style="padding:2px 0">${escapeHtml(v)}</td></tr>`).join('')}</table>
<p style="margin-top:16px">Findings:</p><ul>${findingsHtml}</ul></div>`;
  const text = [intro, '', ...lines.map(([k, v]) => `${k}: ${v}`), '', 'Findings:',
    ...p.findings.map((f) => `- ${f.code}: ${f.message}${f.detail ? ` ${JSON.stringify(f.detail)}` : ''}`)].join('\n');
  return { subject, html, text };
}
